import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  IconButton,
  Box
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { parseISO } from 'date-fns';

function ConfiguracoesNotificacao() {
  const [configs, setConfigs] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [formData, setFormData] = useState({
    tipo: 'pre_vencimento',
    dias: '',
    mensagem_padrao: '',
    hora_execucao: '07:00',
    ativo: true,
    repetir_diariamente: false
  });

  useEffect(() => {
    carregarConfiguracoes();
  }, []);

  const carregarConfiguracoes = async () => {
    try {
      const response = await api.get('/notificacao-configs');
      setConfigs(response.data);
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/notificacao-configs/${selectedConfig.id}`);
      toast.success('Configuração excluída com sucesso');
      setOpenDeleteDialog(false);
      carregarConfiguracoes();
    } catch (error) {
      toast.error('Erro ao excluir configuração');
    }
  };

  const handleOpenDeleteDialog = (config) => {
    setSelectedConfig(config);
    setOpenDeleteDialog(true);
  };

  const handleOpenDialog = (config = null) => {
    if (config) {
      setSelectedConfig(config);
      setFormData({
        tipo: config.tipo,
        dias: config.dias,
        mensagem_padrao: config.mensagem_padrao,
        hora_execucao: config.hora_execucao || '07:00',
        ativo: config.ativo,
        repetir_diariamente: config.repetir_diariamente || false
      });
    } else {
      setSelectedConfig(null);
      setFormData({
        tipo: 'pre_vencimento',
        dias: '',
        mensagem_padrao: '',
        hora_execucao: '07:00',
        ativo: true,
        repetir_diariamente: false
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedConfig(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSwitchChange = (e) => {
    setFormData(prev => ({
      ...prev,
      ativo: e.target.checked
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedConfig) {
        await api.put(`/notificacao-configs/${selectedConfig.id}`, formData);
        toast.success('Configuração atualizada com sucesso');
      } else {
        await api.post('/notificacao-configs', formData);
        toast.success('Configuração criada com sucesso');
      }
      handleCloseDialog();
      carregarConfiguracoes();
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    }
  };

  const getTipoLabel = (tipo) => {
    return tipo === 'pre_vencimento' ? 'Antes do Vencimento' : 'Após Vencimento';
  };

  const handleDateChange = (date) => {
    if (typeof date === 'string') {
      return parseISO(date);
    }
    return date;
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, pl: { xs: 1, md: 1.5 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Configurações de Notificações</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nova Configuração
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tipo</TableCell>
              <TableCell>Dias</TableCell>
              <TableCell>Mensagem Padrão</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.map((config) => (
              <TableRow key={config.id}>
                <TableCell>{getTipoLabel(config.tipo)}</TableCell>
                <TableCell>{config.dias}</TableCell>
                <TableCell>{config.mensagem_padrao}</TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.ativo}
                        onChange={() => {
                          api.put(`/notificacao-configs/${config.id}`, {
                            ...config,
                            ativo: !config.ativo
                          }).then(() => {
                            carregarConfiguracoes();
                            toast.success('Status atualizado com sucesso');
                          }).catch(() => {
                            toast.error('Erro ao atualizar status');
                          });
                        }}
                      />
                    }
                    label={config.ativo ? 'Ativo' : 'Inativo'}
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenDialog(config)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleOpenDeleteDialog(config)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedConfig ? 'Editar Configuração' : 'Nova Configuração'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <FormControl fullWidth margin="normal">
              <InputLabel>Tipo</InputLabel>
              <Select
                name="tipo"
                value={formData.tipo}
                onChange={handleInputChange}
                label="Tipo"
              >
                <MenuItem value="pre_vencimento">Antes do Vencimento</MenuItem>
                <MenuItem value="pos_vencimento">Após Vencimento</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <TextField
                label="Dias"
                type="number"
                value={formData.dias}
                onChange={(e) => setFormData({ ...formData, dias: e.target.value })}
              />
            </FormControl>

            {formData.tipo === 'pos_vencimento' && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.repetir_diariamente}
                      onChange={(e) => setFormData({ ...formData, repetir_diariamente: e.target.checked })}
                    />
                  }
                  label="Repetir notificação todos os dias após o vencimento"
                />
              </FormControl>
            )}

            <FormControl fullWidth sx={{ mt: 2 }}>
              <TextField
                name="mensagem_padrao"
                label="Mensagem Padrão"
                multiline
                rows={4}
                value={formData.mensagem_padrao || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  mensagem_padrao: e.target.value
                }))}
                helperText="Use {cliente} para nome do cliente e {valor} para valor da parcela"
                variant="outlined"
              />
            </FormControl>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <TextField
                name="hora_execucao"
                label="Horário de Execução"
                type="time"
                value={formData.hora_execucao || '07:00'}
                onChange={e => setFormData(prev => ({
                  ...prev,
                  hora_execucao: e.target.value
                }))}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }} // minutos
                helperText="Exemplo: 07:00"
                required
              />
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.ativo}
                  onChange={handleSwitchChange}
                  name="ativo"
                />
              }
              label="Ativo"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {selectedConfig ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir esta configuração de notificação?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ConfiguracoesNotificacao;
